import React, { useState } from "react";
import { Button } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import Utils from "../../../../shared/helpers/utils";

const str = val => String(val ?? '');
const bool = val => val === true ? 'Si' : 'No';

const sectionTitle = (text) => ({
  text,
  bold: true,
  fontSize: 8,
  margin: [10, 6, 0, 2],
  color: '#555555',
});

const subTable = (headers, rows, widths) => ({
  margin: [10, 0, 0, 4],
  table: {
    headerRows: 1,
    widths: widths || Array(headers.length).fill('*'),
    body: [
      headers.map(h => ({ text: h, bold: true, fillColor: '#aaaaaa', color: '#fff', fontSize: 6 })),
      ...rows,
    ],
  },
  layout: {
    hLineWidth: () => 0.5,
    vLineWidth: () => 0.5,
    hLineColor: () => '#cccccc',
    vLineColor: () => '#cccccc',
  },
});

const ExportInformeEstablecimientoAnexoVPDF = ({
  filteredData,
  sustancias,
  establecimientosEmpresa,
  unidadesMedida,
  anexoi,
  cuit,
}) => {
  const [loading, setLoading] = useState(false);

  const getEstablecimiento = (id) => {
    if (!anexoi?.EstablecimientosDeclarados) return null;
    return anexoi.EstablecimientosDeclarados.find(x => x.IdEstablecimientoEmpresa == id);
  };

  const downloadPDF = () => {
    setLoading(true);
    setTimeout(() => {
      const today = new Date();
      const formattedDate = today.toLocaleString(undefined, { hour12: false });

      // --- Agrupar sustancias filtradas por establecimiento ---
      const porEstablecimiento = {};
      (filteredData || []).forEach(s => {
        const idEst = s.IdEstablecimientoEmpresa;
        if (!porEstablecimiento[idEst]) porEstablecimiento[idEst] = [];
        porEstablecimiento[idEst].push(s);
      });

      const content = [
        {
          text: 'Informe por Establecimiento - Sustancias',
          bold: true,
          fontSize: 18,
          alignment: 'center',
          margin: [0, 0, 0, 5],
        },
        {
          columns: [
            { text: `CUIT: ${cuit || ''}`, fontSize: 10, alignment: 'left' },
            { text: `Fecha de generacion: ${formattedDate}`, fontSize: 10, alignment: 'right' },
          ],
          margin: [0, 0, 0, 4],
        },
        {
          text: `Total de sustancias (filtradas): ${(filteredData || []).length}`,
          fontSize: 9,
          alignment: 'left',
          margin: [0, 0, 0, 10],
          color: '#555555',
        },
        {
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 760, y2: 0, lineWidth: 0.5 }],
          margin: [0, 0, 0, 14],
        },
      ];

      // --- Una sección por cada establecimiento ---
      Object.keys(porEstablecimiento).forEach((idEst, idx) => {
        const sustanciasDelEst = porEstablecimiento[idEst];
        const estDesc = str(Utils.getIdDesc(idEst, establecimientosEmpresa));
        const establecimiento = getEstablecimiento(idEst);
        const puestos = establecimiento?.Puestos || [];

        // Título del establecimiento
        content.push({
          text: `Establecimiento: ${estDesc}`,
          bold: true,
          fontSize: 13,
          margin: idx === 0 ? [0, 0, 0, 3] : [0, 18, 0, 3],
          color: '#1890ff',
        });

        content.push({
          text: `Total de sustancias: ${sustanciasDelEst.length}`,
          fontSize: 9,
          margin: [0, 0, 0, 6],
          color: '#555555',
        });

        // Tabla principal de sustancias del establecimiento
        const sustanciasBody = [
          [
            { text: 'Id',              bold: true, fillColor: '#1890ff', color: '#fff', fontSize: 7 },
            { text: 'Sustancia',       bold: true, fillColor: '#1890ff', color: '#fff', fontSize: 7 },
            { text: 'Nombre Comercial',bold: true, fillColor: '#1890ff', color: '#fff', fontSize: 7 },
            { text: 'Cantidad Anual',  bold: true, fillColor: '#1890ff', color: '#fff', fontSize: 7 },
            { text: 'Unidad de Medida',bold: true, fillColor: '#1890ff', color: '#fff', fontSize: 7 },
          ]
        ];

        sustanciasDelEst.forEach(s => {
          sustanciasBody.push([
            { text: str(s.IdSustanciaDeclarada), fontSize: 7 },
            { text: str(Utils.getIdDesc(s.IdSustancia, sustancias)), fontSize: 7 },
            { text: str(s.NombreComercial), fontSize: 7 },
            { text: str(s.CantidadAnual), fontSize: 7 },
            { text: str(Utils.getIdDesc(s.IdUnidadDeMedida, unidadesMedida)), fontSize: 7 },
          ]);
        });

        content.push({
          table: {
            headerRows: 1,
            widths: ['auto', '*', 'auto', 'auto', 'auto'],
            body: sustanciasBody,
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#cccccc',
            vLineColor: () => '#cccccc',
          },
        });

        // --- Detalle por sustancia (sub-secciones) ---
        sustanciasDelEst.forEach(s => {
          const tieneDatos =
            (s.Utilizaciones?.length > 0) ||
            (s.PuestosAfectados?.length > 0) ||
            (s.EquiposRadiologicos?.length > 0) ||
            (s.Proveedores?.length > 0) ||
            (s.Compradores?.length > 0) ||
            (s.EstudiosAmbientalesEspecificos?.length > 0) ||
            (s.EstudiosBiologicosEspecificos?.length > 0);

          if (!tieneDatos) return;

          // Subtítulo de la sustancia
          content.push({
            text: `  Sustancia ${str(s.IdSustanciaDeclarada)} — ${str(Utils.getIdDesc(s.IdSustancia, sustancias))}${s.NombreComercial ? ' | ' + s.NombreComercial : ''}`,
            bold: true,
            fontSize: 9,
            margin: [0, 10, 0, 3],
            color: '#333333',
          });

          // Utilizaciones
          if (s.Utilizaciones?.length > 0) {
            content.push(sectionTitle('Utilizaciones'));
            content.push(subTable(
              ['Id', 'Uso Origen', 'Modo de Empleo'],
              s.Utilizaciones.map(u => [
                { text: str(u.IdUtilizacion), fontSize: 6 },
                { text: str(u.UsoOrigen), fontSize: 6 },
                { text: str(u.ModoDeEmpleo), fontSize: 6 },
              ]),
              ['auto', '*', '*']
            ));
          }

          // Puestos Afectados
          if (s.PuestosAfectados?.length > 0) {
            content.push(sectionTitle('Puestos Afectados'));
            content.push(subTable(
              ['Id', 'Puesto', 'Desc. Actividad', 'Inf. Riesgos', 'Cap. Riesgos', 'Entrega EPP', 'Desc. Estudios', 'Lic. Especial'],
              s.PuestosAfectados.map(p => [
                { text: str(p.IdPuestoAfectado), fontSize: 6 },
                { text: str(Utils.getIdDesc(p.IdPuesto, puestos)), fontSize: 6 },
                { text: str(p.DescripcionActividad), fontSize: 6 },
                { text: bool(p.InformaSobreRiesgos), fontSize: 6 },
                { text: bool(p.CapacitacionSobreRiesgos), fontSize: 6 },
                { text: bool(p.EntregaElementosDeProteccion), fontSize: 6 },
                { text: str(p.DescripcionEstudios), fontSize: 6 },
                { text: bool(p.LicenciaEspecial), fontSize: 6 },
              ]),
              ['auto', '*', '*', 'auto', 'auto', 'auto', '*', 'auto']
            ));
          }

          // Equipos Radiologicos
          if (s.EquiposRadiologicos?.length > 0) {
            content.push(sectionTitle('Equipos Radiologicos'));
            content.push(subTable(
              ['Id', 'Marca', 'Modelo', 'Tipo Equipo', 'Tipo Radiacion', 'Actividad', 'Radioisotopo', 'KVP', 'mAp', 'MeV', 'Observaciones'],
              s.EquiposRadiologicos.map(e => [
                { text: str(e.IdEquipoRadiologico), fontSize: 6 },
                { text: str(e.Marca), fontSize: 6 },
                { text: str(e.Modelo), fontSize: 6 },
                { text: str(e.IdTipoEquipo), fontSize: 6 },
                { text: str(e.IdTipoRadiacion), fontSize: 6 },
                { text: str(e.Actividad), fontSize: 6 },
                { text: str(e.RadioisotopoFuente), fontSize: 6 },
                { text: str(e.PotenciaEquipoKVP), fontSize: 6 },
                { text: str(e.PotenciaEquipomAp), fontSize: 6 },
                { text: str(e.PotenciaEquipoMeV), fontSize: 6 },
                { text: str(e.Observaciones), fontSize: 6 },
              ]),
              ['auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', '*']
            ));
          }

          // Proveedores
          if (s.Proveedores?.length > 0) {
            content.push(sectionTitle('Proveedores'));
            content.push(subTable(
              ['Id', 'CUIT', 'Nombre Comercial'],
              s.Proveedores.map(p => [
                { text: str(p.IdProveedor), fontSize: 6 },
                { text: str(p.Cuit), fontSize: 6 },
                { text: str(p.NombreComercial), fontSize: 6 },
              ]),
              ['auto', 'auto', '*']
            ));
          }

          // Compradores
          if (s.Compradores?.length > 0) {
            content.push(sectionTitle('Compradores'));
            content.push(subTable(
              ['Id', 'CUIT', 'Nombre Comercial'],
              s.Compradores.map(c => [
                { text: str(c.IdComprador), fontSize: 6 },
                { text: str(c.Cuit), fontSize: 6 },
                { text: str(c.NombreComercial), fontSize: 6 },
              ]),
              ['auto', 'auto', '*']
            ));
          }

          // Estudios Ambientales Especificos
          if (s.EstudiosAmbientalesEspecificos?.length > 0) {
            content.push(sectionTitle('Estudios Ambientales Especificos'));
            content.push(subTable(
              ['Id', 'Cant. Frecuencia', 'Unidad Frecuencia', 'Metodologia Empleada'],
              s.EstudiosAmbientalesEspecificos.map(e => [
                { text: str(e.IdEstudioAmbientalEspecifico), fontSize: 6 },
                { text: str(e.CantidadFrecuencia), fontSize: 6 },
                { text: str(e.IdUnidadFrecuencia), fontSize: 6 },
                { text: str(e.MetodologiaEmpleada), fontSize: 6 },
              ]),
              ['auto', 'auto', 'auto', '*']
            ));
          }

          // Estudios Biologicos Especificos
          if (s.EstudiosBiologicosEspecificos?.length > 0) {
            content.push(sectionTitle('Estudios Biologicos Especificos'));
            content.push(subTable(
              ['Id', 'Cant. Frecuencia', 'Unidad Frecuencia', 'Analisis Estudios Complementacion'],
              s.EstudiosBiologicosEspecificos.map(e => [
                { text: str(e.IdEstudioBiologicoEspecifico), fontSize: 6 },
                { text: str(e.CantidadFrecuencia), fontSize: 6 },
                { text: str(e.IdUnidadFrecuencia), fontSize: 6 },
                { text: str(e.AnalisisEstudiosComplementacion), fontSize: 6 },
              ]),
              ['auto', 'auto', 'auto', '*']
            ));
          }
        });
      });

      const documentDefinition = {
        pageOrientation: 'landscape',
        pageMargins: [30, 50, 30, 30],
        content,
        footer: (currentPage, pageCount) => ({
          columns: [
            { text: `Pagina ${currentPage} de ${pageCount}`, alignment: 'left', fontSize: 8, margin: [30, 0, 0, 0] },
            { text: `Impreso: ${formattedDate}`, alignment: 'right', fontSize: 8, margin: [0, 0, 30, 0] },
          ],
        }),
      };

      pdfMake.createPdf(documentDefinition).download(`informe_establecimientos_sustancias_${cuit || 'export'}.pdf`, () => {
        setLoading(false);
      });
    }, 0);
  };

  return (
    <Button
      onClick={downloadPDF}
      type="default"
      icon={<FileTextOutlined />}
      loading={loading}
    >
      Informe por Establecimiento
    </Button>
  );
};

export default ExportInformeEstablecimientoAnexoVPDF;
